import React, { useContext, useMemo } from 'react';
import EnhancedChart from './MultiChart';
import { Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import { appointmentTypeLabel, AppointmentTypeRef } from '../utils/outpatientAppointmentTypes';

// Interfaces for wait time data
export interface IWaitTimeDistribution {
  withinWeek: number;
  withinTwoWeeks: number;
  withinMonth: number;
  overMonth: number;
}

export interface IAppointmentTypeWaitTimeStats {
  appointmentType: AppointmentTypeRef;
  departmentUuid: string;
  departmentName: string;
  totalAppointments: number;
  avgWaitTime: number;
  minWaitTime: number;
  maxWaitTime: number;
  medianWaitTime: number;
  percentile90WaitTime: number;
  waitTimeDistribution: IWaitTimeDistribution;
  waitTimeDistributionPercentage: IWaitTimeDistribution;
}

export interface IAppointmentWaitTimeStats {
  status: number;
  waitTimeStats?: IAppointmentTypeWaitTimeStats[];
  /** SHER-1049: the types present in `waitTimeStats`, in display order. */
  appointmentTypes?: AppointmentTypeRef[];
  errorMessage?: string;
}

interface AppointmentWaitTimeChartProps {
  data: IAppointmentWaitTimeStats;
  title?: string;
  horizontal?: boolean;
  metricType?: 'avg' | 'median' | 'percentile90' | 'min' | 'max';
}

const AppointmentWaitTimeChart = ({
  data,
  title,
  horizontal = true,
  metricType = 'avg'
}: AppointmentWaitTimeChartProps) => {
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  
  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!data.waitTimeStats || data.waitTimeStats.length === 0) {
      return {
        series: [],
        departments: [],
        totalAppointments: 0
      };
    }
    
    // Get unique departments and appointment types
    const departments = data.waitTimeStats && Array.isArray(data.waitTimeStats)
      ? [...new Set(data.waitTimeStats.map(item => item.departmentName))].sort((a, b) => (a || '').localeCompare(b || ''))
      : [];
    const appointmentTypes = data.appointmentTypes || [];
    
    // Calculate total appointments
    const totalAppointments = data.waitTimeStats && Array.isArray(data.waitTimeStats)
      ? data.waitTimeStats.reduce((sum, item) => sum + (item?.totalAppointments || 0), 0)
      : 0;
    
    // Get the appropriate metric value based on the selected metric type
    const getMetricValue = (item: IAppointmentTypeWaitTimeStats) => {
      let value: number;
      switch (metricType) {
        case 'avg': 
          value = Math.max(1, item.avgWaitTime); // Ensure non-negative
          return Math.round(value); // Round to nearest integer
        case 'median': return item.medianWaitTime;
        case 'percentile90': return item.percentile90WaitTime;
        case 'min': return item.minWaitTime;
        case 'max': return item.maxWaitTime;
        default: 
          value = Math.max(0, item.avgWaitTime); // Ensure non-negative
          return Math.round(value); // Round to nearest integer
      }
    };
    
    // Create series data for each appointment type
    const series = appointmentTypes.map(type => {
      const typeData = departments.map(dept => {
        const match = data.waitTimeStats?.find(item => 
          item.departmentName === dept && 
          item.appointmentType.key === type.key
        );
        return match ? Number(getMetricValue(match).toFixed(1)) : 0;
      });
      
      return {
        name: appointmentTypeLabel(type, translate, 'short'),
        data: typeData
      };
    });
    
    return {
      series,
      departments,
      totalAppointments
    };
  }, [data, metricType, translate]);
  
  // The card used to build its own English sentence here and ignore the `title` prop it
  // already accepted, so this header stayed English on a French tenant while the table
  // beside it translated. The caller now supplies the label.
  const titleComponent = (
    <Typography variant="h6" fontWeight={600}>
      {title || <Translate id="hospital.analytics.wait_time.average_title" />}
    </Typography>
  );
  
  const chartProps = {
    series: chartData.series,
    title: titleComponent,
    categories: chartData.departments,
    type: 'bar' as 'bar' | 'line' | 'area' | 'radar' | 'scatter' | 'heatmap' | 'pie' | 'donut',
    stacked: false,
    horizontal,
    total: -1
  };

  return <EnhancedChart {...chartProps} />;
};

export default AppointmentWaitTimeChart;